/* eslint-disable @typescript-eslint/no-explicit-any */
const DB_NAME = 'TOPPION_ORG_DB';
const DB_VERSION = 260403; // ← chỉ tăng khi thêm/sửa schema
// dbService.ts

export function openDB(dbName = DB_NAME, version = 1, stores: { name: string, keyPath: string }[] = []) {

    version = DB_VERSION;

    return new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open(dbName, version);

        request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            stores.forEach(store => {
                if (!db.objectStoreNames.contains(store.name)) {
                    db.createObjectStore(store.name, { keyPath: store.keyPath });
                }
            });
        };

        request.onsuccess = () => {
            const db = request.result;

            // ✅ Nhường chỗ khi tab khác upgrade
            db.onversionchange = () => {
                db.close();
            };

            resolve(db);
        };

        request.onblocked = () => reject(new Error("IndexedDB blocked"));
        request.onerror = () => reject(request.error);
    });
}

export function openDB1(dbName = DB_NAME, version = 1, stores: { name: string, keyPath: string }[] = []) {
    return new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open(dbName, version);

        // ✅ Thêm handler này
        request.onblocked = () => {
            console.warn('IndexedDB bị block — có tab khác đang giữ kết nối cũ');
            reject(new Error('IndexedDB open blocked'));
        };

        request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            stores.forEach(store => {
                if (!db.objectStoreNames.contains(store.name)) {
                    db.createObjectStore(store.name, { keyPath: store.keyPath });
                }
            });
        };

        request.onsuccess = () => {
            const db = request.result;

            // ✅ Lắng nghe versionchange để tự đóng khi tab khác cần upgrade
            db.onversionchange = () => {
                db.close();
            };

            // If the DB already exists but missing some stores, bump version to create them
            const missingStores = (stores || []).filter(s => !db.objectStoreNames.contains(s.name));
            if (missingStores.length > 0) {
                const nextVersion = db.version + 1;
                db.close();
                const upgradeReq = indexedDB.open(dbName, nextVersion);
                upgradeReq.onupgradeneeded = (e) => {
                    const udb = (e.target as IDBOpenDBRequest).result;
                    missingStores.forEach(store => {
                        if (!udb.objectStoreNames.contains(store.name)) {
                            udb.createObjectStore(store.name, { keyPath: store.keyPath });
                        }
                    });
                };
                upgradeReq.onsuccess = () => resolve(upgradeReq.result);
                upgradeReq.onerror = () => reject(upgradeReq.error);
                return;
            }
            resolve(db);
        };
        request.onerror = () => reject(request.error);
    });
}

function ensurePrimaryKey(storeName: string, record: any): any {
    if (storeName === 'organization') {
        // For organization store, keep the original record as is
        return record;
    }
    // For other stores, ensure Id field exists
    if (storeName === 'employees' || storeName === 'departments' || storeName === 'positions' || storeName === 'units') {
        const idValue = record?.Id ?? record?.id ?? record?.ID;
        return { ...record, Id: idValue };
    }
    return record;
}

/** Đảm bảo DB có store 'organization' (tạo nếu chưa có). */
async function ensureOrganizationStore(): Promise<IDBDatabase> {
    const db = await openDB(DB_NAME);
    if (db.objectStoreNames.contains('organization')) return db;
    db.close();
    return openDB(DB_NAME, db.version + 1, [{ name: 'organization', keyPath: 'id' }]);
}

export async function putRecord<T>(storeName: string, record: T & { [key: string]: any }) {
    const db = storeName === 'organization'
        ? await ensureOrganizationStore()
        : await openDB(DB_NAME);

    if (!db.objectStoreNames.contains(storeName)) {
        db.close();
        throw new Error(`Object store '${storeName}' does not exist`);
    }

    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    const normalized = ensurePrimaryKey(storeName, record);

    if (storeName === 'organization') {
        store.put(normalized);
    } else {
        store.put({ ...normalized, updatedAt: Date.now() });
    }

    return new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

export async function getAllRecords<T>(storeName: string): Promise<T[]> {
    const db = await openDB(DB_NAME);
    
    // Check if object store exists
    if (!db.objectStoreNames.contains(storeName)) {
        throw new Error(`Object store '${storeName}' does not exist`);
    }
    
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readonly");
        const store = tx.objectStore(storeName);
        const req = store.getAll();
        req.onsuccess = () => resolve(req.result as T[]);
        req.onerror = () => reject(req.error);
    });
}

export async function getRecordById<T>(storeName: string, id: any): Promise<T | null> {
    const db = storeName === 'organization'
        ? await ensureOrganizationStore()
        : await openDB(DB_NAME);

    if (!db.objectStoreNames.contains(storeName)) {
        return null;
    }

    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readonly");
        const store = tx.objectStore(storeName);
        const req = store.get(id);
        req.onsuccess = () => resolve(req.result as T || null);
        req.onerror = () => reject(req.error);
    });
}

export async function deleteRecord(storeName: string, id: any) {
    const db = storeName === 'organization'
        ? await ensureOrganizationStore()
        : await openDB(DB_NAME);

    if (!db.objectStoreNames.contains(storeName)) {
        return;
    }

    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    store.delete(id);
    return new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}
export async function clearDatabase(): Promise<boolean> {
    return openDB().then(function (db) {
        return new Promise(function (resolve) {
            const tx = db.transaction(DB_NAME, 'readwrite');
            const store = tx.objectStore(DB_NAME);
            const req = store.clear();
            req.onsuccess = function () {
                resolve(true);
            };
            req.onerror = function () {
                resolve(false);
            };
        });
    });
}

export async function deleteDatabase(): Promise<boolean> {
    try {
        const db = await openDB();
        if (db) {
            db.close();
        }
    } catch (e) {
        // ignore if db cannot be opened
    }
    return new Promise<boolean>((resolve, reject) => {
        const deleteReq = window.indexedDB.deleteDatabase(DB_NAME);
        deleteReq.onsuccess = function () {
            resolve(true);
        };
        deleteReq.onerror = function () {
            resolve(false); // Resolve với false thay vì reject để không throw error
        };
        deleteReq.onblocked = function () {
            // Đợi một chút rồi thử lại hoặc resolve false
            setTimeout(() => {
                resolve(false);
            }, 1000);
        };
    });
}
